import { createFileRoute, Link } from '@tanstack/react-router'
import { Hero } from '@/components/home/hero'
import { MainPageFooter } from '@/components/MainPageFooter'
import { JoinAPresentationWidget } from '@/components/home/JoinAPresentationWidget';
import { ChurchAppSection } from '@/components/home/ChurchAppSection';

declare global {
  namespace React.JSX {
    interface IntrinsicElements {
      'umi-newsletter-widget': any;
    }
  }
}

const sectionStyles = {
  default: 'bg-gradient-to-br from-purple-50 to-orange-50 dark:from-purple-950/30 dark:to-orange-950/30 border-y border-stone-200 dark:border-mist-800',
  black: 'bg-stone-900 text-white',
} as const

// When twoCol is true, expects exactly two children: stacked on mobile, side by side (equal widths, centered) on md+.
function ColoredSectionWrapper({ children, twoCol = false, bg = 'default' }: { children: React.ReactNode; twoCol?: boolean; bg?: keyof typeof sectionStyles }) {
  return (
    <div className={`py-20 ${sectionStyles[bg]}`}>
      {twoCol ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start max-w-5xl mx-auto px-6">
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  )
}

function HostAndJoinSection() {
  /* Host Section */
  return (
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-6 text-stone-900 dark:text-white">Want to share your own slides?</h2>
            <p className="text-lg text-stone-600 dark:text-stone-400 mb-8 leading-relaxed max-w-2xl mx-auto">
              If you lead a classroom, church service, or meeting, you can easily broadcast your slides to your audience's personal devices at the same time as to the projector.
              Keep everyone on the same page and encourage active note-taking.
            </p>
            <p className="text-lg text-stone-600 dark:text-stone-400 mb-8 leading-relaxed max-w-2xl mx-auto">
              No more audience trying to point their cellphones at the screen to take a picture!
            </p>
            <div className="flex justify-center gap-4">
              <Link to="/capture-app" className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5">
                Hosting tools
              </Link>
              {/* <Link to="/signup" className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5">
                Create Free Account
              </Link> */}
            </div>
          </div>);
}

export const Route = createFileRoute('/')({
  component: LandingPage,
})

function LandingPage() {
  return (
    <div className="min-h-screen flex flex-col font-sans bg-stone-50 dark:bg-mist-950 text-stone-900 dark:text-white">

      <main className="flex-grow">
        {/* Hero Section */}
        <Hero />

        <ColoredSectionWrapper twoCol>
          <JoinAPresentationWidget />
          <HostAndJoinSection />
        </ColoredSectionWrapper>


        {/* Newsletter Section */}
        <section className="py-20 max-w-5xl mx-auto px-6 border-t border-stone-100 dark:border-mist-800">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-stone-900 dark:text-white">We are growing fast</h2>
              <p className="text-lg text-stone-600 dark:text-stone-400 mb-4 leading-relaxed">
                Sermon Slides is in beta testing with select churches and ministries. We expect to be fully launched January 2027.
              </p>
              <p className="text-lg text-stone-600 dark:text-stone-400 leading-relaxed">
                If you are adventurous, or just curious, get our newsletter for important milestone updates.
              </p>
              <p className="text-lg text-stone-600 dark:text-stone-400 leading-relaxed">
                To get on our beta, sign up, and we will notify you when you are eligible.
              </p>
            </div>
            <div className="bg-white dark:bg-mist-900 p-8 rounded-2xl border border-stone-200 dark:border-mist-800 shadow-sm">
              <umi-newsletter-widget product-id="sermon-slides"></umi-newsletter-widget>
            </div>
          </div>
        </section>

        <ColoredSectionWrapper bg="black">
          <ChurchAppSection />
        </ColoredSectionWrapper>


        {/* FAQ Section */}
        <section className="py-20 max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-10 text-center text-stone-900 dark:text-white">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <details className="group bg-white dark:bg-mist-900 border border-stone-200 dark:border-mist-800 rounded-xl overflow-hidden [&_summary::-webkit-details-marker]:hidden">
              <summary className="flex cursor-pointer items-center justify-between gap-1.5 p-6 text-stone-900 dark:text-white font-medium">
                <h3 className="text-lg">Do attendees need to download an app?</h3>
                <span className="shrink-0 rounded-full bg-purple-50 dark:bg-purple-950/40 p-1.5 text-purple-700 group-open:-rotate-180 transition-transform duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" className="size-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </span>
              </summary>
              <div className="px-6 pb-6 pt-0 text-stone-600 dark:text-stone-400 leading-relaxed">
                No, participants can just enter the joining code on this website through their mobile browser. No downloads required!
              </div>
            </details>

            <details className="group bg-white dark:bg-mist-900 border border-stone-200 dark:border-mist-800 rounded-xl overflow-hidden [&_summary::-webkit-details-marker]:hidden">
              <summary className="flex cursor-pointer items-center justify-between gap-1.5 p-6 text-stone-900 dark:text-white font-medium">
                <h3 className="text-lg">Does it integrate with ProPresenter or PowerPoint?</h3>
                <span className="shrink-0 rounded-full bg-purple-50 dark:bg-purple-950/40 p-1.5 text-purple-700 group-open:-rotate-180 transition-transform duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" className="size-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </span>
              </summary>
              <div className="px-6 pb-6 pt-0 text-stone-600 dark:text-stone-400 leading-relaxed">
                Yes! Our desktop software captures your presentation output seamlessly and syncs it to the cloud in real-time.
              </div>
            </details>

            <details className="group bg-white dark:bg-mist-900 border border-stone-200 dark:border-mist-800 rounded-xl overflow-hidden [&_summary::-webkit-details-marker]:hidden">
              <summary className="flex cursor-pointer items-center justify-between gap-1.5 p-6 text-stone-900 dark:text-white font-medium">
                <h3 className="text-lg">How much does it cost?</h3>
                <span className="shrink-0 rounded-full bg-purple-50 dark:bg-purple-950/40 p-1.5 text-purple-700 group-open:-rotate-180 transition-transform duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" className="size-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </span>
              </summary>
              <div className="px-6 pb-6 pt-0 text-stone-600 dark:text-stone-400 leading-relaxed">
                We offer a free tier for small groups. For larger congregations and classrooms, we have affordable premium plans to support higher concurrency.
              </div>
            </details>
          </div>
        </section>

        {/* Training Section */}
        <ColoredSectionWrapper bg="black">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-2xl font-bold mb-4">Want to learn more?</h2>
            <p className="text-stone-300 dark:text-mist-600 mb-8 max-w-2xl mx-auto">
              Check out our comprehensive documentation to learn how to set up your screens, cameras, and configure integrations for the best note-taking experience.
            </p>
            <a href="https://learn.sermon-slides.com/docs" className="inline-flex items-center gap-2 border border-stone-500 hover:border-white hover:bg-stone-800 dark:bg-mist-700 text-white px-6 py-3 rounded-lg font-medium transition-colors">
              Read the Documentation
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </ColoredSectionWrapper>
      </main>

      <MainPageFooter />
    </div>
  )
}
